import { trackEvent } from '@helpers/analytics';
import {
  Badge,
  InfoNoFillIcon,
  Popover,
  Typography,
  TypographyType,
} from '@vivino/js-react-common-ui';
import { Currency, priceToString, useI18N } from '@vivino/js-web-common';
import cx from 'classnames';
import React, { useState } from 'react';

import BottleRibbon from '@components/icons/BottleRibbon/BottleRibbon';

import styles from './freeBottleDiscountBanner.scss';

const TRANSLATION_PATH = 'components.vivino_plus';
const TRANSLATIONS = {
  premium: 'common.premium',
  giftBottlePerk: `${TRANSLATION_PATH}.gift_bottle_perk`,
  getAgiftBottle: `${TRANSLATION_PATH}.you_will_get_a_gift_bottle`,
  addBottles: `${TRANSLATION_PATH}.add_bottles_to_get_one_free`,
  oneOfYourBottlesIsOnUs: `${TRANSLATION_PATH}.one_of_your_bottles_is_on_us`,
  whenYouBuyXBottlesOrMore: `${TRANSLATION_PATH}.when_you_buy_x_bottles_or_more`,
};

interface FreeBottleDiscountBannerPresentationalProps {
  bottlesRequired: number;
  maxAmount: number;
  currency: Currency;
  className?: string;
  bottleCount?: number;
}

const FreeBottleDiscountBannerPresentational = ({
  bottlesRequired,
  maxAmount,
  currency,
  className,
  bottleCount,
}: FreeBottleDiscountBannerPresentationalProps) => {
  const { t } = useI18N();
  const [anchorEl, setAnchorEl] = useState(null);

  const maxAmountWithPrefix = priceToString({ amount: maxAmount, currency });

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    trackEvent({
      event: 'Bottle perk - Info - Show',
    });
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const showGiftBottlePerk = bottlesRequired - Number(bottleCount) <= 0;
  return (
    <div
      className={cx(styles.freeBottleDiscountBanner, className)}
      data-testid="freeBottleDiscountBanner"
    >
      <div className={styles.container}>
        <div className={styles.title}>
          <div className={styles.titleItem}>
            <Badge label={t(TRANSLATIONS.premium)} variant="outlined" size="medium" />
          </div>
          <div className={styles.textContainer}>
            <Typography
              type={TypographyType.LabelLarge}
              component="div"
              className={styles.titleItem}
            >
              {t(TRANSLATIONS.giftBottlePerk)}
            </Typography>

            <div className={styles.titleItem}>
              <InfoNoFillIcon
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onClick={handlePopoverOpen}
                data-testid="infoIcon"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
              />
              <Popover
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                className={styles.popover}
              >
                <Typography type={TypographyType.LabelLarge} component="div">
                  {t(TRANSLATIONS.oneOfYourBottlesIsOnUs)}
                </Typography>
                <Typography type={TypographyType.BodyMedium} component="div">
                  {t(TRANSLATIONS.whenYouBuyXBottlesOrMore, {
                    bottles_count: bottlesRequired,
                    max_amount: maxAmountWithPrefix,
                  })}
                </Typography>
              </Popover>
            </div>
          </div>
        </div>
        <BottleRibbon />
      </div>
      <Typography type={TypographyType.BodySmall} component="div">
        {showGiftBottlePerk
          ? t(TRANSLATIONS.getAgiftBottle)
          : t(TRANSLATIONS.addBottles, { bottle_count: bottlesRequired - Number(bottleCount) })}
      </Typography>
    </div>
  );
};

export default FreeBottleDiscountBannerPresentational;
